import SheetSearchController from "../lib/sheet-search-controller";

/**
 * The home club sheet: choosing a club writes its id into the registration and its name onto the
 * row that opened the sheet.
 *
 * Two hidden fields rather than one. The id is what the registration is submitted with; the name is
 * what this step and the summary show, and carrying it saves reading the club back out of the search
 * index on every request that has to draw it.
 */
export default class extends SheetSearchController {
  /* The base's own values are spread back in for the type checker's benefit. Stimulus reads
     `values` up the prototype chain and would find `url` either way; TypeScript compares the two
     static shapes and takes a subclass that redeclares the property as narrowing it away. */
  static override values = {
    ...SheetSearchController.values,
    hiddenId: String,
    nameHiddenId: String,
    valueId: String,
  };

  declare readonly hiddenIdValue: string;
  declare readonly nameHiddenIdValue: string;
  declare readonly valueIdValue: string;

  /**
   * The two `input`/`change` events are for anything watching the fields: a hidden input set by
   * script fires neither by itself.
   *
   * The ids are handed over as values rather than reached for as targets, because the field and the
   * sheet are siblings and a target has to sit inside its own controller's element.
   */
  override onPick(event: Event): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const row = target.closest<HTMLElement>("[data-club-id]");
    if (row === null) {
      return;
    }

    const name = row.dataset.clubName ?? "";

    this.write(this.hiddenIdValue, row.dataset.clubId ?? "");
    this.write(this.nameHiddenIdValue, name);

    /* `data-chosen` is what tells the stylesheet this is a value and not the prompt it started as,
       so the two read differently without this having to know which class says so. */
    const value = document.getElementById(this.valueIdValue);
    if (value !== null) {
      value.textContent = name;
      value.setAttribute("data-chosen", "");
    }

    this.element.close();
  }

  private write(id: string, value: string): void {
    const field = document.getElementById(id);
    if (!(field instanceof HTMLInputElement)) {
      return;
    }

    field.value = value;
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
  }
}
